import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import {
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaExclamationCircle,
  FaPencilAlt,
  FaQuestion,
} from 'react-icons/fa';
import { FaBarcode, FaFilter, FaPix } from 'react-icons/fa6';
import { IoAdd } from 'react-icons/io5';
import {
  LuArchive,
  LuArchiveRestore,
  LuChevronDown,
  LuChevronUp,
  LuTrash2,
} from 'react-icons/lu';

import FORMATTERS from 'helpers/formatters';
import { ChargeForm } from '_components/_shared';
import { ChargeFilters, ChargesSummary } from '_components/_shared';
import { Button, Card, Popover, Tag } from '_components/_core';
import { ItemsPerPage, Pagination } from '_components/_core/Table/components';
import { useTableV2 } from '_components/_core/Table/utils';

import { StyledTable, FiltersCount, StyledButton } from './styles.js';
import { convertBase64ToBlob } from '../../utilities';

const tableConfig = {
  defaultFilters: {},
  defaultSorting: { field: 'due_date', order: 'desc' },
  defaultPagination: { currentPage: 1, itemsPerPage: 10 },
};

const countFilters = filters => {
  return Object.entries(filters).filter(([key, value]) => key && value).length;
};

const PAYMENT_METHOD_NAMES = {
  PIX: 'Pix',
  CREDIT_CARD: 'Cartão',
  BOLETO: 'Boleto',
  UNDEFINED: 'Indefinido',
};

const PAYMENT_METHOD_ICONS = {
  PIX: <FaPix className="mr-2" />,
  CREDIT_CARD: <FaCreditCard className="mr-2" />,
  BOLETO: <FaBarcode className="mr-2" />,
  UNDEFINED: <FaQuestion className="mr-2" />,
};

function ChargesList({
  charges,
  totalCharges,
  onCreateCharge,
  onDeleteCharge,
  onFetchCharges,
  onArchiveCharge,
  onUnarchiveCharge,
  onFetchAvailablePaymentGateways,
  onFetchAvailablePaymentMethods,
  onFetchAvailableChargeStatus,
  onFetchChargesSummary,
  activeCompany,
}) {
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const history = useHistory();

  const hasActiveFilters = !isEmpty(selectedFilters);

  const handleFetchData = useCallback(
    params => {
      onFetchCharges(params);
    },
    [onFetchCharges],
  );

  useEffect(() => {
    onFetchAvailablePaymentGateways();
    onFetchAvailablePaymentMethods();
    onFetchAvailableChargeStatus();
  }, [
    onFetchAvailablePaymentGateways,
    onFetchAvailablePaymentMethods,
    onFetchAvailableChargeStatus,
    activeCompany,
  ]);

  const {
    pagination,
    onFilter,
    onPageChange,
    onPageSizeChange,
    onSorting,
    sorting,
    onRefresh,
  } = useTableV2({
    ...tableConfig,
    data: charges,
    onFetchData: handleFetchData,
    withSavedFilters: false,
    withInitialLoading: false,
  });

  const CHARGE_TYPES = {
    RECURRING: 'recorrente',
    SINGLE: 'avulsa',
    INSTALLMENT: 'parcelada',
  };

  const CHARGE_TYPES_VARIANT = {
    RECURRING: 'default',
    SINGLE: 'default',
    INSTALLMENT: 'default',
  };

  const isPastDue = useCallback(charge => {
    const today = DateTime.now().startOf('day');

    const field = 'due_date';

    let dateValue = null;

    if (has(charge, field)) {
      dateValue = charge[field];
    } else {
      dateValue = charge.due_date;
    }

    return DateTime.fromISO(dateValue) < today;
  }, []);

  const getPaidIcon = useCallback(
    charge => {
      if (!charge) {
        return null;
      }

      if (isPastDue(charge) && !charge.payment_date) {
        return (
          <Popover
            trigger="hover"
            title="Vencida"
            content={<p className="m-0">Esta cobrança está com o pagamento atrasado.</p>}
            placement="bottom-start"
          >
            <FaExclamationCircle className="text-danger float-right" />
          </Popover>
        );
      }

      if (charge.payment_date) {
        return (
          <Popover
            trigger="hover"
            title="Recebida"
            content={
              <p className="m-0">
                Recebida em {FORMATTERS.DATE_DDMMYYYY(charge.payment_date)}
              </p>
            }
            placement="bottom-start"
          >
            <FaCheckCircle className="text-success float-right" />
          </Popover>
        );
      }

      return (
        <Popover
          trigger="hover"
          title={charge.status_description}
          content={<p className="m-0">Esta cobrança está aguardando pagamento.</p>}
          placement="bottom-start"
        >
          <FaClock className="text-warning float-right" />
        </Popover>
      );
    },
    [isPastDue],
  );

  const handleAddCharge = useCallback(() => {
    setSelectedCharge(null);

    setIsFormOpen(true);
  }, []);

  const handleEditCharge = useCallback(
    id => {
      const charge = charges.find(charge => charge.id === id);
      setSelectedCharge(charge);
      setIsFormOpen(true);
    },
    [charges],
  );

  const handleDeleteCharge = useCallback(
    id => {
      const charge = charges.find(charge => charge.id === id);

      onDeleteCharge(charge);
    },
    [charges, onDeleteCharge],
  );

  const handleArchiveCharge = useCallback(
    id => {
      const charge = charges.find(charge => charge.id === id);

      onArchiveCharge(charge);
    },
    [charges, onArchiveCharge],
  );

  const handleUnarchiveCharge = useCallback(
    id => {
      const charge = charges.find(charge => charge.id === id);

      onUnarchiveCharge(charge);
    },
    [charges, onUnarchiveCharge],
  );

  const handleChargeClick = useCallback(
    id => {
      history.push(`/cobrancas/${id}/detalhe`);
    },
    [history],
  );

  const handleDownloadCharge = useCallback(charge => {
    const { company_payment_gateway_mnemonic } = charge;

    switch (company_payment_gateway_mnemonic) {
      case 'ASAAS':
      case 'ASAAS_SANDBOX':
        window.open(charge.payment_url, '_blank');
        break;
      case 'BANCO_INTER': {
        const { boleto_base64 } = charge;

        const fileName = `${charge.recipient_name} ${charge.description}`;

        convertBase64ToBlob(boleto_base64, fileName);
        break;
      }

      default:
        break;
    }
  }, []);

  const handleOpenFilters = useCallback(() => {
    setIsFilterOpen(true);
  }, []);

  const handleFilter = useCallback(
    values => {
      setSelectedFilters(values);

      onFilter(values);
    },
    [onFilter],
  );

  const renderSorting = useCallback(
    field => {
      if (sorting.field === field) {
        return (
          <span>{sorting.order === 'asc' ? <LuChevronUp /> : <LuChevronDown />}</span>
        );
      }

      return null;
    },
    [sorting],
  );

  const sortableColumn = useCallback(
    (column_name, column_value, ...props) => {
      const order =
        sorting.field === column_value
          ? sorting.order === 'asc'
            ? 'desc'
            : 'asc'
          : 'asc';

      return (
        <th
          onClick={() => onSorting({ field: column_value, order })}
          {...props}
          style={{ cursor: 'pointer' }}
        >
          <span className="d-flex align-items-center justify-content-between">
            <span>{column_name}</span>
            {renderSorting(column_value)}
          </span>
        </th>
      );
    },
    [sorting, onSorting, renderSorting],
  );

  return (
    <>
      <ChargeForm
        isOpen={isFormOpen}
        charge={selectedCharge}
        onToggleForm={() => setIsFormOpen(!isFormOpen)}
        onCreateCharge={onCreateCharge}
        onAfterSaveCallback={() => {
          onRefresh();
          onFetchChargesSummary();
          setIsFormOpen(false);
        }}
        onDeleteCharge={onDeleteCharge}
        onFetchCharges={() => onFetchCharges(selectedFilters)}
        title={selectedCharge ? 'Editar cobrança' : 'Criar cobrança'}
      />

      <div className="d-flex justify-content-between align-items-end mb-3">
        <h4>Cobranças</h4>
        <div className="d-flex align-items-center">
          <Button
            variant="success-2"
            icon={<IoAdd size="1.2em" />}
            iconPosition="left"
            disabled={false}
            onClick={handleAddCharge}
            name="add-charge-button"
            className="mr-2"
            size="sm"
          >
            Nova Cobrança
          </Button>
          <div className="position-relative d-inline-block ml-2">
            <StyledButton
              variant="inverse-dark"
              icon={<FaFilter size="1.2em" />}
              iconPosition="left"
              disabled={false}
              onClick={handleOpenFilters}
              size="sm"
            >
              Filtros
              {hasActiveFilters && countFilters(selectedFilters) > 0 && (
                <FiltersCount className="ml-2">
                  {countFilters(selectedFilters)}
                </FiltersCount>
              )}
            </StyledButton>
          </div>
        </div>
      </div>
      <div>
        <ChargeFilters
          isOpen={isFilterOpen}
          onToggle={() => setIsFilterOpen(!isFilterOpen)}
          onFilter={handleFilter}
        />
      </div>
      <ChargesSummary />
      <Row>
        <Col>
          <Card>
            <StyledTable className="table table-hover">
              <thead>
                <tr>
                  {sortableColumn('Vencimento', 'due_date', {
                    width: '130px',
                  })}
                  {sortableColumn('Recebida em', 'payment_date', {
                    width: '130px',
                    className: 'text-center',
                  })}
                  {sortableColumn('Cobrança / Cliente', 'recipient_name')}
                  {sortableColumn('Valor', 'amount', {
                    width: '120px',
                    className: 'text-center',
                  })}
                  {sortableColumn('Forma Pagamento', 'payment_method_name', {
                    width: '150px',
                    className: 'text-center',
                  })}
                  {sortableColumn('Status', 'status_description', {
                    width: '120px',
                    className: 'text-center',
                  })}
                  {sortableColumn('Ações', 'actions', {
                    width: '120px',
                    className: 'text-center',
                  })}
                </tr>
              </thead>
              <tbody>
                {!isEmpty(charges) &&
                  charges.map(charge => {
                    return (
                      <tr key={charge.id}>
                        <td width="130px">
                          <div className="d-flex align-items-center justify-content-between">
                            <span>{FORMATTERS.DATE_DDMMYYYY(charge.due_date)}</span>
                            {getPaidIcon(charge)}
                          </div>
                        </td>
                        <td width="130px" className="text-center">
                          <span>{FORMATTERS.DATE_DDMMYYYY(charge.payment_date)}</span>
                        </td>
                        <td>
                          <span className="d-flex align-items-center">
                            <span className="d-flex flex-column align-items-start">
                              <span>{charge.recipient_name}</span>
                              <span>
                                <Button
                                  variant="link"
                                  onClick={() => handleChargeClick(charge.id)}
                                  size="sm"
                                  className="p-0 m-0"
                                >
                                  <small>{charge.description}</small>
                                </Button>
                              </span>
                            </span>
                            {charge.charge_type !== 'SINGLE' && (
                              <span className="ml-auto mr-2">
                                <Tag
                                  variant={CHARGE_TYPES_VARIANT[charge.charge_type]}
                                  className="p-0 m-0 ml-auto pl-2 pr-2"
                                >
                                  {CHARGE_TYPES[charge.charge_type]}
                                </Tag>
                              </span>
                            )}
                          </span>
                        </td>
                        <td width="120px" className="text-center">
                          <span className="font-weight-bold financial-value">
                            {FORMATTERS.NUMBER(charge.amount)}
                          </span>
                        </td>

                        <td width="150px" className="text-center">
                          <span className="d-flex align-items-center justify-content-center">
                            {PAYMENT_METHOD_ICONS[charge.payment_method_mnemonic]}
                            {PAYMENT_METHOD_NAMES[charge.payment_method_mnemonic]}
                          </span>
                        </td>
                        <td width="120px" className="text-center">
                          <Tag
                            className="p-0 m-0 ml-auto pl-2 pr-2"
                            variant={charge.status_variant}
                          >
                            {charge.status_description}
                          </Tag>
                        </td>
                        <td width="120px" className="text-center">
                          <div className="d-flex align-items-center justify-content-center">
                            <Button
                              variant="white"
                              className="d-flex align-items-center justify-content-center p-0 m-0 pt-1 pb-1 pl-1 pr-1 mr-1"
                              size="sm"
                              onClick={() => handleEditCharge(charge.id)}
                            >
                              <FaPencilAlt className="text-info" />
                            </Button>
                            <Button
                              variant="white"
                              className="d-flex align-items-center justify-content-center p-0 m-0 pt-1 pb-1 pl-1 pr-1 mr-1"
                              size="sm"
                              onClick={() => handleDownloadCharge(charge)}
                            >
                              <FaBarcode className="text-success" />
                            </Button>
                            {charge.archived_at && (
                              <Button
                                variant="white"
                                className="d-flex align-items-center justify-content-center p-0 m-0 pt-1 pb-1 pl-1 pr-1 mr-1"
                                size="sm"
                                onClick={() => handleUnarchiveCharge(charge.id)}
                              >
                                <LuArchiveRestore className="text-success" />
                              </Button>
                            )}
                            {!charge.archived_at && (
                              <Button
                                variant="white"
                                className="d-flex align-items-center justify-content-center p-0 m-0 pt-1 pb-1 pl-1 pr-1 mr-1"
                                size="sm"
                                onClick={() => handleArchiveCharge(charge.id)}
                              >
                                <LuArchive className="text-muted" />
                              </Button>
                            )}
                            <Button
                              variant="white"
                              className="d-flex align-items-center justify-content-center p-0 m-0 pt-1 pb-1 pl-1 pr-1 mr-1"
                              size="sm"
                              onClick={() => handleDeleteCharge(charge.id)}
                            >
                              <LuTrash2 className="text-danger" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </StyledTable>
          </Card>
          <div className="d-flex justify-content-between align-items-center">
            <ItemsPerPage
              itemsPerPage={pagination.itemsPerPage}
              onChange={onPageSizeChange}
              noMarginsOnTotals
              className="mr-3"
              total={totalCharges}
              totalBeingShown={charges.length}
              maxItemsPerPage={20}
            />
            <Pagination
              {...pagination}
              total={totalCharges}
              onPageChange={onPageChange}
            />
          </div>
        </Col>
      </Row>
    </>
  );
}

ChargesList.propTypes = {
  charges: PropTypes.array.isRequired,
  totalCharges: PropTypes.number.isRequired,
  onCreateCharge: PropTypes.func.isRequired,
  onDeleteCharge: PropTypes.func.isRequired,
  onFetchCharges: PropTypes.func.isRequired,
  onArchiveCharge: PropTypes.func.isRequired,
  onUnarchiveCharge: PropTypes.func.isRequired,
  onFetchAvailablePaymentGateways: PropTypes.func.isRequired,
  onFetchAvailablePaymentMethods: PropTypes.func.isRequired,
  onFetchAvailableChargeStatus: PropTypes.func.isRequired,
  onFetchChargesSummary: PropTypes.func.isRequired,
  activeCompany: PropTypes.object,
  isLoading: PropTypes.bool,
  recipients: PropTypes.array,
  paymentMethods: PropTypes.array,
};

ChargesList.defaultProps = {
  isLoading: false,
  paymentMethods: [],
  recipients: [],
  charges: [],
  totalCharges: 0,
};

export default ChargesList;
